import React, {useCallback, useEffect, useMemo, useState} from "react";
import PopUp from "../../../../PopUp/Loadable";
import {LossAversionPopup, contentStyle, overlayStyle, LossAversionButtonArea} from "../styled";
import {Button} from "../../../../HTMLEntities/Button";
import {useSelector} from "react-redux";
import {RootStore} from "../../../../../configureStore";
import {aversionMessage} from "../../../../../containers/RegistrationLoginPage/utils";
import {useHistory} from "react-router";
import {EDIT_PROFILE, EXTERNAL_LINK, VIEWED_RESPONSE} from "../../../../../types";
import {trackClickEvent} from "../../../../../utils/utils";
import config from "../../../../../config/config";
import {redirectToURL} from "../../../../CTA/response";
import {validateField} from "../../../../../utils/validation";
import {postMessageApp} from "../../../../../utils/AppHelper";

interface LossAversionLayerProps {
    otpLayer?: "otpLayer";
    showAversionLayer: boolean;
    exitAversionLayer(): void;
    closeOnlyAversionLayer(): void;
    pageIdentifier?: string;
}

const LossAversionLayer: React.FC<LossAversionLayerProps> = ({
    otpLayer,
    showAversionLayer,
    exitAversionLayer,
    closeOnlyAversionLayer,
    pageIdentifier,
}: LossAversionLayerProps) => {
    const [showPopup, setShowPopup] = useState(showAversionLayer);
    const currentPage = useSelector((state: RootStore) => state.layout.currentPageName);
    const isMobile = useSelector((state: RootStore) => state.layout.isMobile);
    const gaEventCategory = `${pageIdentifier ? pageIdentifier : currentPage ?? ""}_${
        isMobile ? "Mobile" : "Desktop"
    }`;

    useEffect(() => {
        setShowPopup(showAversionLayer);
    }, [showAversionLayer]);
    const ctaTrackingData = useSelector(
        (state: RootStore) => state.RegistrationLoginPage.ctaTrackingData,
    );
    const ctaType = useSelector((state: RootStore) => state.RegistrationLoginPage.ctaType);
    const style = useMemo(() => {
        return {
            content: {
                ...contentStyle,
                width: isMobile ? "320px" : "350px",
            },
            overlay: overlayStyle,
        };
    }, [isMobile]);

    let msg: string = aversionMessage["registration"];
    if (otpLayer) {
        msg = aversionMessage[otpLayer];
    } else if (currentPage === "loginPage") {
        msg = aversionMessage["login"];
    } else if (
        ctaType &&
        ctaType !== VIEWED_RESPONSE &&
        ctaType !== EDIT_PROFILE &&
        ctaType !== EXTERNAL_LINK
    ) {
        msg = aversionMessage["cta"][ctaType];
    }

    useEffect(() => {
        // GA Tracking
        if (showAversionLayer) {
            trackClickEvent(gaEventCategory, "LossAversionLayer");
        }
    }, [showAversionLayer, gaEventCategory]);

    const history = useHistory();
    const exitAversionLayerHandler = useCallback(() => {
        // GA Tracking
        trackClickEvent(gaEventCategory ?? "", "LossAversionLayerExitBtnClick");
        window.onbeforeunload = null;

        //@typescript-eslint/restrict-template-expressions
        const finalRefUrl =
            ctaTrackingData !== undefined
                ? ctaTrackingData && ctaTrackingData.referralURL
                    ? ctaTrackingData.trackingId === 11583 || ctaTrackingData.trackingId === 11585
                        ? `${config.SHIKSHA_STUDYABROAD_HOME}`
                        : /^(https?:\/\/|www\.)/.test(ctaTrackingData.referralURL)
                        ? `${ctaTrackingData.referralURL}` // Referral URL contains domain
                        : `${config.SHIKSHA_STUDYABROAD_HOME}${ctaTrackingData.referralURL}` // Referral URL does not contain domain
                    : ""
                : "";
        // const ctaURL = ctaTrackingData !== undefined ? ctaTrackingData?.referralURL ?? "" : "";
        // const finalRefUrl = `${config.SHIKSHA_STUDYABROAD_HOME}${ctaURL}`;

        postMessageApp({
            closeAppWebView: true,
        });

        if (validateField(finalRefUrl)) {
            redirectToURL({
                url: finalRefUrl,
                isPWA: false,
                history,
            });
        } else {
            history.goBack();
        }
    }, [gaEventCategory, history, ctaTrackingData]);

    const closeHandler = useCallback(() => {
        // GA Tracking
        trackClickEvent(gaEventCategory ?? "", "LossAversionLayerStayBtnClick");
        closeOnlyAversionLayer();
    }, [gaEventCategory, closeOnlyAversionLayer]);

    return useMemo(() => {
        return (
            <PopUp
                popUpName="lossAversion"
                isOpen={showPopup}
                style={style}
                shouldCloseOnOverlayClick={false}
                showHeader={false}
                showHeading={false}
                closeHandle={() => {
                    setShowPopup(false);
                    window.onbeforeunload = null;
                    exitAversionLayer();
                }}
                enableEscape={false}
                preventDefaultBack={false}
                disableBack={false}
            >
                <LossAversionPopup>
                    <p>This is a mandatory step, by skipping it you will not be able to {msg}.</p>
                    <p>Are you sure you want to skip?</p>
                    <LossAversionButtonArea>
                        <Button
                            onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                exitAversionLayerHandler();
                                event.stopPropagation();
                            }}
                        >
                            Yes exit
                        </Button>
                        <Button orange onClick={closeHandler}>
                            Stay on this form
                        </Button>
                    </LossAversionButtonArea>
                </LossAversionPopup>
            </PopUp>
        );
    }, [closeHandler, exitAversionLayerHandler, msg, style, exitAversionLayer, showPopup]);
};

export default LossAversionLayer;
